import { ArrowRight, CalendarDays, Clock } from 'lucide-react';
import { useEffect, useRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { formatDate, formatDateTime } from '../../shared/format';
import { Input, Label } from './field';

export function TimeRange({
  start,
  end,
  mode = 'datetime',
}: {
  readonly start: string;
  readonly end?: string;
  readonly mode?: 'date' | 'datetime';
}) {
  const startLabel = mode === 'date' ? formatDate(start) : formatDateTime(start);
  const endLabel = end ? (mode === 'date' ? formatDate(end) : formatDateTime(end)) : 'Sin salida';
  const Icon = mode === 'date' ? CalendarDays : Clock;
  return (
    <div className="inline-grid gap-1 rounded-md border border-[#d6ded0] bg-white px-3 py-2 text-sm text-[#334033]">
      <span className="flex items-center gap-2 text-xs font-semibold uppercase text-[#687461]">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        Rango
      </span>
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-[#223126]">{startLabel}</span>
        <span className="text-[#8b9485]">a</span>
        <span className="font-semibold text-[#223126]">{endLabel}</span>
      </span>
    </div>
  );
}

export function DateRangeInputs({
  start,
  end,
  startLabel,
  endLabel,
  mode = 'date',
  allowEqual = false,
  children,
}: {
  readonly start: InputHTMLAttributes<HTMLInputElement> & { readonly id: string };
  readonly end: InputHTMLAttributes<HTMLInputElement> & { readonly id: string };
  readonly startLabel: string;
  readonly endLabel: string;
  readonly mode?: 'date' | 'datetime';
  readonly allowEqual?: boolean;
  readonly children?: ReactNode;
}) {
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);
  const startValue = typeof start.value === 'string' ? start.value : '';
  const endValue = typeof end.value === 'string' ? end.value : '';
  const startTime = new Date(startValue).getTime();
  const endTime = new Date(endValue).getTime();
  const hasInvalidRange = Boolean(startValue && endValue && (allowEqual ? startTime > endTime : startTime >= endTime));
  const inputType = mode === 'date' ? 'date' : 'datetime-local';
  const timeHint = mode === 'date' ? 'Fecha' : 'Fecha y hora';
  const validationMessage = hasInvalidRange ? 'La fecha y hora inicial debe ser anterior a la fecha y hora final.' : '';

  useEffect(() => {
    startRef.current?.setCustomValidity(validationMessage);
    endRef.current?.setCustomValidity(validationMessage);
  }, [validationMessage]);

  return (
    <div className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3">
      <div className="grid items-start gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <div className="grid gap-1.5">
          <Label htmlFor={start.id} className="flex items-center gap-2 text-xs font-semibold uppercase">
            <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
            {startLabel}
          </Label>
          <Input {...start} ref={startRef} type={inputType} max={endValue || start.max} step={mode === 'datetime' ? 60 : start.step} aria-invalid={hasInvalidRange || start['aria-invalid']} />
          <p className="text-xs text-[#687461]">{timeHint} de inicio</p>
        </div>
        <div className="hidden pt-8 text-[#75816a] md:block">
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={end.id} className="flex items-center gap-2 text-xs font-semibold uppercase">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {endLabel}
          </Label>
          <Input {...end} ref={endRef} type={inputType} min={startValue || end.min} step={mode === 'datetime' ? 60 : end.step} aria-invalid={hasInvalidRange || end['aria-invalid']} />
          <p className="text-xs text-[#687461]">{timeHint} de fin</p>
        </div>
      </div>
      {hasInvalidRange ? <p className="mt-3 text-sm font-medium text-rose-600">La fecha y hora inicial debe ser anterior a la fecha y hora final.</p> : null}
      {children ? <div className="mt-3 grid gap-2">{children}</div> : null}
    </div>
  );
}

export function todayDateInput() {
  return new Date().toISOString().slice(0, 10);
}

export function localDateTimeInput(date = new Date()) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export function addDateUnits(value: string, amount: number, unit: 'days' | 'hours') {
  const base = value ? new Date(value) : new Date();
  if (Number.isNaN(base.getTime())) return value;
  const next = new Date(base);
  if (unit === 'days') next.setDate(next.getDate() + amount);
  if (unit === 'hours') next.setHours(next.getHours() + amount);
  return unit === 'days' ? next.toISOString().slice(0, 10) : localDateTimeInput(next);
}

export function DateShortcutButtons({
  start,
  unit,
  onToday,
  onAdd,
}: {
  readonly start?: string;
  readonly unit: 'days' | 'hours';
  readonly onToday: () => void;
  readonly onAdd?: (value: string) => void;
}) {
  const chips = unit === 'days' ? [1, 2, 3, 7] : [1, 2, 3, 5];
  return (
    <div className="flex flex-wrap gap-2">
      <button type="button" className="h-8 rounded-md border border-[#d6ded0] bg-white px-3 text-xs font-semibold text-[#334033] hover:bg-[#eef3ea]" onClick={onToday}>
        Hoy
      </button>
      {onAdd ? chips.map((amount) => (
        <button key={amount} type="button" className="h-8 rounded-md border border-[#d6ded0] bg-[#fbfcfa] px-3 text-xs font-semibold text-[#4f5d49] hover:bg-[#eef3ea]" onClick={() => onAdd(addDateUnits(start ?? '', amount, unit))}>
          +{amount} {unit === 'days' ? 'd' : 'h'}
        </button>
      )) : null}
    </div>
  );
}
